import { useEffect, useState } from 'react'

import { CUSTOM_CSS_PREVIEW_CLEARED } from '~/portal/hooks/useCustomCssPreviewSync'
import { getCustomCssPreviewChannelName, getCustomCssPreviewStorageKey } from '~/portal/utils/shared/customCssPreview'

export const useCustomCssPreviewOverride = (portalSubdomain: string, isPreviewActive: boolean): string | undefined => {
  const storageKey = getCustomCssPreviewStorageKey(portalSubdomain)

  // Starts empty on purpose: the server can't see the draft, so reading localStorage during the
  // first render makes the hydrating client differ from the server HTML. In a production build
  // React doesn't patch mismatched attributes or dangerouslySetInnerHTML, so the draft would be
  // silently dropped on any full page load. The draft is loaded in the effect below instead.
  const [draftCss, setDraftCss] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!isPreviewActive) {
      return
    }

    setDraftCss(localStorage.getItem(storageKey) ?? undefined)

    const channel = new BroadcastChannel(getCustomCssPreviewChannelName(portalSubdomain))
    channel.onmessage = (event: MessageEvent<string>) => {
      setDraftCss(event.data === CUSTOM_CSS_PREVIEW_CLEARED ? undefined : event.data)
    }

    const onStorage = (event: StorageEvent): void => {
      if (event.key === storageKey) {
        setDraftCss(event.newValue ?? undefined)
      }
    }
    window.addEventListener('storage', onStorage)

    return () => {
      channel.close()
      window.removeEventListener('storage', onStorage)
    }
  }, [isPreviewActive, portalSubdomain, storageKey])

  return isPreviewActive ? draftCss : undefined
}
